import { GetServerSidePropsContext } from 'next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import {
  fetchSearchResults,
  getFooterLinks,
  getFilterOptions
} from '../../new-lib/apis';
import SearchContainer from '../../features/search-new/index';
import { getSelectorsByUserAgent } from 'react-device-detect';
import {
  setExperimentVariant,
  setIsMobile
} from '../../redux/actions/appActions';
import { wrapper } from '../../redux/store';
import { SearchViewTypes } from '../../new-lib/constants/common';
import Mapbox from '../../features/map/Mapbox';
import { setMapConfig } from '../../redux/slices/mapSlice';
import { useRouter } from 'next/router';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useSearchContext } from '../../features/search-new/context/SearchContext';
import { SearchFiltersActionTypes } from '../../reducers/search-filters/actions';
import {
  constructSsrSearchBody,
  constructNewSsrSearchBody
} from '../../helpers/search-filters';
import SchemaSearch from '../../helpers/schema/schema-search';
import { ConfigsApis } from '../../new-lib/apis/configs.apis';
import SchemaCompoundSearch from '../../helpers/schema/schema-search-compound';
import { MapsApis } from '../../new-lib/apis/maps.apis';
import { AreasApis } from '../../new-lib/apis/areas.apis';
import { DevelopersApis } from '../../new-lib/apis/developers.apis';
import { SearchApis } from '../../new-lib/apis/search.apis';

export default function Search(props: any) {
  const { query } = useRouter();
  const { searchFiltersDispatch } = useSearchContext();
  const prevRoute = useSelector((state: any) => state.app.referer);
  const isMapView = query?.view === SearchViewTypes.MAP;

  useEffect(() => {
    if (prevRoute?.includes('/nawy-now')) {
      searchFiltersDispatch({
        type: SearchFiltersActionTypes.RESET
      });
    }
  }, [prevRoute]);

  if (isMapView) {
    return <Mapbox />;
  } else {
    return <SearchContainer {...props} />;
  }
}
export const getServerSideProps = wrapper.getServerSideProps(
  store =>
    async ({ req, locale, query, resolvedUrl }: GetServerSidePropsContext) => {
      try {
        const filters = constructNewSsrSearchBody(query);
        const isProperty = query?.category === 'property';
        const isMapView = query?.view === SearchViewTypes.MAP;
        const [
          footerLinks,
          configVariables,
          filterOptions,
          initialResults,
          areas,
          developers,
          mapConfig,
          metaTags
        ] = await Promise.all([
          getFooterLinks(locale, 'search'),
          ConfigsApis.getConfigs(locale),
          getFilterOptions(locale),
          isProperty
            ? fetchSearchResults(constructSsrSearchBody(query), locale).then(
                ({ data }) => data
              )
            : SearchApis.newCompoundsSearch(filters, locale),
          AreasApis.listAll(locale),
          DevelopersApis.listAll(locale),
          MapsApis.getMapConfig(),
          isMapView
            ? SearchApis.getMapMetaTags(filters, locale)
            : SearchApis.getMetaTags(
                isProperty ? 'properties' : 'compounds',
                {},
                locale
              )
        ]);

        const isMobile = getSelectorsByUserAgent(
          req.headers['user-agent']!
        ).isMobile;

        store.dispatch(setIsMobile(isMobile));
        store.dispatch(setExperimentVariant(req.cookies['_ab'] as string));
        store.dispatch(setMapConfig(mapConfig));

        const schema = isProperty
          ? new SchemaSearch(initialResults)
          : new SchemaCompoundSearch(initialResults);

        return {
          props: {
            ...(await serverSideTranslations(locale as string, [
              'search',
              'common',
              'contact',
              'launches',
              'compare',
              'map',
              'entity'
            ])),
            isMobile,
            filterOptions: JSON.stringify(filterOptions) || '',
            configVariables,
            layout: {
              title: metaTags?.metaTitle,
              url: `${process.env.NEXT_PUBLIC_HOST}/search`,
              alternatePaths: {
                en: `${process.env.NEXT_PUBLIC_HOST}${resolvedUrl}`,
                ar: `${process.env.NEXT_PUBLIC_HOST}/ar${resolvedUrl}`
              },
              isMobile,
              footerLinks,
              metaDescription: metaTags?.metaDescription,
              noFollow: true
            },
            loadedSearchResultsSSR: initialResults,
            searchCategory: query?.category || null,
            developers,
            schema: schema?.getSchema()
          }
        };
      } catch (err) {
        return {
          notFound: true
        };
      }
    }
);
